import type {
  Entry,
  Grade,
  GradedEpisode,
  PitchSubmission,
  Pitch,
  PostPublic,
} from "../../desk-records";
import { postIdFromNotes, linkPitchPosts } from "../../entry/pitch-discussion";
import { entryMemberUrl, mediaKindOf } from "../../entry/pitch-entry";
import { gradeFromResults } from "./pitch-grades";

type JoinInput = {
  submissions: PitchSubmission[];
  episodes: GradedEpisode[];
  entries: Record<string, Entry | null>;
  posts: PostPublic[];
};

/** One row per entry, newest grade wins, posts matched through `notes` or, failing that, by text. */
export function joinPitches({
  submissions,
  episodes,
  entries,
  posts,
}: JoinInput): Pitch[] {
  const newestFirst = episodes.toSorted((a, b) =>
    b.completedAt.localeCompare(a.completedAt),
  );
  const seen = new Set<string>();
  const pitches: Pitch[] = [];

  for (const submission of submissions) {
    const pv = submission.policy_version;

    if (seen.has(pv.id)) continue;

    if (submission.status === "withdrawn" || submission.status === "rejected")
      continue;

    seen.add(pv.id);
    const entry = entries[pv.id] ?? null;
    const notedId = postIdFromNotes(submission.notes);
    const text = entry?.text ?? "";
    let grade: Grade | null = null;

    for (const episode of newestFirst) {
      grade = gradeFromResults({ episode, policyVersionId: pv.id });

      if (grade) break;
    }

    pitches.push({
      policyVersionId: pv.id,
      policyLabel: pv.label,
      playerId: submission.player?.id ?? pv.player_id ?? null,
      playerName: submission.player?.name ?? "unknown player",
      filedAt: submission.created_at,
      text,
      media: (entry?.media ?? []).map((item) => ({
        kind: mediaKindOf(item.path),
        url: entryMemberUrl({ policyVersionId: pv.id, path: item.path }),
        alt: item.alt_text,
      })),
      entryUnread: entry === null,
      postId: notedId,
      authorUserId: null,
      ships: 0,
      grade,
      status: submission.status,
    });
  }

  return linkPitchPosts({ pitches, posts });
}
